import { useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@tanstack/react-query';
import { ICreateProductDTO } from '@legacy/shared-types/dto/product.type';
import { ProductCategoryEnum } from '@legacy/shared-types/enums/product-category.enum';
import { ProductTypeEnum } from '@legacy/shared-types/enums/product-type.enum';
import { FormCalendar } from '@components/common/FormCalendar/FormCalendar';
import { FormCheckbox } from '@components/common/FormCheckbox/FormCheckbox';
import { FormControlLabel } from '@components/common/FormControlLabel';
import { FormSelect } from '@components/common/FormSelect/FormSelect';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { InputAdornment } from '@components/common/InputAdornment';
import { LoadingButton } from '@components/common/LoadingButton/LoadingButton';
import { ISelectOption } from '@components/common/Select/types';
import { ICreateProductForm } from './types';
import { CreateProductDefaultValues, formatProductForClone, productFormValuesFormatter } from './helpers';
import { productApi } from '@api/product/productsApi';
import { useBankOfEnglandRate } from '@queries/useBankOfEnglandRate';
import { useProductById } from '@queries/products/useProductById';
import { PageURL } from '@router/pageURL';
import { useFormSelectOptions } from '../useFormSelectOptions';
import { FormNumberField } from '@components/common/FormNumberField/FormNumberField';
export const AddProductPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneFromProductId = searchParams.get('cloneFromProductId') ?? undefined;
  const { data: currentRate } = useBankOfEnglandRate();
  const {
    data: productToClone,
    isLoading: isCloneLoading,
    isError: hasCloneError,
  } = useProductById(cloneFromProductId);
  const { mutate: createNewProduct, isLoading } = useMutation(productApi.createProduct);
  const {
    productTypeOptions,
    productPoolOptions,
    productRangeOptions,
    productCategoryOptions,
    btlExpatOptions,
    propertyCategoryOptions,
  } = useFormSelectOptions();
  const { enqueueSnackbar } = useSnackbar();
  const clonedProductTypeRef = useRef<ProductTypeEnum | null>(null);

  const form = useForm<ICreateProductForm>({
    defaultValues: CreateProductDefaultValues,
  });

  // Watch for changes in all form inputs
  const formState = form.watch();

  useEffect(() => {
    if (!productToClone) {
      return;
    }

    clonedProductTypeRef.current = productToClone.productType;
    form.reset(formatProductForClone(productToClone));
  }, [form, productToClone]);

  const submitFormHandler = async (formData: ICreateProductForm) => {
    const data = Object.entries(formData).reduce((acc: Record<string, any>, [key, value]) => {
      acc[key as keyof ICreateProductForm] = productFormValuesFormatter(key, value);

      return acc;
    }, {}) as ICreateProductDTO;

    if (data.productCategory !== ProductCategoryEnum.BUY_TO_LET) {
      delete data.isExpatriate;
      delete data.propertyCategory;
    }

    createNewProduct(data, {
      onSuccess: () => navigate(PageURL.PRODUCTS),
      onError: (error: any) => {
        const messages = error.message.split(';');
        messages.forEach((message: string) => enqueueSnackbar(message, { variant: 'error' }));
      },
    });
  };

  useEffect(() => {
    const isApplyingCloneDefaults = clonedProductTypeRef.current === formState.productType;

    if (isApplyingCloneDefaults) {
      clonedProductTypeRef.current = null;
    } else if (formState.productType === ProductTypeEnum.DISCOUNT && currentRate) {
      form.setValue('initialBaseRate', (currentRate * 100).toFixed(2));
    }

    if (!isApplyingCloneDefaults && formState.productType === ProductTypeEnum.FIXED) {
      form.setValue('initialMargin', 0);
    }
  }, [form, formState.productType, currentRate]);

  useEffect(() => {
    form.setValue('initialRate', Number(formState.initialBaseRate) + Number(formState.initialMargin) || 0);
  }, [form, formState.initialBaseRate, formState.initialMargin]);

  useEffect(() => {
    if (formState.productCategory !== ProductCategoryEnum.BUY_TO_LET) {
      form.setValue('isExpatriate', null);
      form.setValue('propertyCategory', null);
    }
  }, [form, formState.productCategory]);

  if (cloneFromProductId && isCloneLoading) {
    return <h1>Loading...</h1>;
  }

  if (cloneFromProductId && hasCloneError) {
    return <h1>Could not load product to clone</h1>;
  }

  return (
    <div className={'mx-auto w-fit'}>
      <h1>Add new product</h1>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitFormHandler)} className={'flex flex-col items-center'}>
          <div className={'mb-8 grid grid-cols-4 gap-8'}>
            <div className={'mt-4'}>
              <FormTextField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.salesforceId.label' }),
                }}
                name={'salesforceId'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (Number(value) <= 0 || !value.match(/^[0-9]+$/))
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });

                    if (value.length > 10) return intl.formatMessage({ id: 'product.salesforceId.max' });
                  },
                }}
              />
            </div>
            <div className={'mt-4'}>
              <FormTextField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.description.label' }),
                }}
                name={'description'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  maxLength: {
                    value: 80,
                    message: intl.formatMessage({ id: 'product.description.limit' }, { limit: 80 }),
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productCategory'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productCategoryOptions}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                  label: intl.formatMessage({ id: 'product.productCategory.label' }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productRange'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productRangeOptions}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                  label: intl.formatMessage({ id: 'product.productRange.label' }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productType'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productTypeOptions}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                  label: intl.formatMessage({ id: 'product.productType.label' }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.monthsInitial.label' }),
                }}
                name={'monthsInitial'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (parseFloat(value) % 1) {
                      return intl.formatMessage({ id: 'common.error.integerField' });
                    }

                    if (Number(value) < 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.maximumFTV.label' }),
                  inputProps: { step: 0.1 },
                  InputProps: { endAdornment: <InputAdornment position='end'>%</InputAdornment> },
                }}
                name={'maximumFTV'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (Number(value) <= 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.maxFti.label' }),
                  inputProps: { step: 0.1, min: 0, max: 10 },
                }}
                name={'maxFti'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    const n = Number(value);
                    if (Number.isNaN(n)) {
                      return intl.formatMessage({ id: 'common.error.requiredField' });
                    }
                    if (n < 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                    if (n > 10) {
                      return intl.formatMessage({ id: 'common.error.maxNumber' }, { max: 10 });
                    }
                  },
                }}
              />
            </div>

            {formState.productCategory === ProductCategoryEnum.BUY_TO_LET ? (
              <>
                <div className={'mt-4'}>
                  <FormSelect
                    name={'isExpatriate'}
                    rules={{
                      validate: (value: boolean | null) => {
                        if (formState.productCategory !== ProductCategoryEnum.BUY_TO_LET) {
                          return true;
                        }
                        if (value === null || value === undefined) {
                          return intl.formatMessage({ id: 'common.error.requiredField' });
                        }
                        return true;
                      },
                    }}
                    options={btlExpatOptions}
                    selectProps={{
                      isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                        option.value === selectedOption.value,
                    }}
                    selectInputProps={{
                      placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                      label: intl.formatMessage({ id: 'product.isExpatriate.label' }),
                      InputLabelProps: { shrink: true },
                    }}
                  />
                </div>
                <div className={'mt-4'}>
                  <FormSelect
                    name={'propertyCategory'}
                    rules={{
                      validate: (value: string | null) => {
                        if (formState.productCategory !== ProductCategoryEnum.BUY_TO_LET) {
                          return true;
                        }
                        if (value === null || value === undefined || value === '') {
                          return intl.formatMessage({ id: 'common.error.requiredField' });
                        }
                        return true;
                      },
                    }}
                    options={propertyCategoryOptions}
                    selectProps={{
                      isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                        option.value === selectedOption.value,
                    }}
                    selectInputProps={{
                      placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                      label: intl.formatMessage({ id: 'product.propertyCategory.label' }),
                      InputLabelProps: { shrink: true },
                    }}
                  />
                </div>
              </>
            ) : null}

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: formState.productType === 'DISCOUNT',
                  label: intl.formatMessage({ id: 'product.initialBaseRate.label' }),
                  inputProps: { step: 0.1 },
                  InputProps: { endAdornment: <InputAdornment position='end'>%</InputAdornment> },
                }}
                name={'initialBaseRate'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (Number(value) <= 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  disabled: formState.productType === ProductTypeEnum.FIXED,
                  label: intl.formatMessage({ id: 'product.initialMargin.label' }),
                  inputProps: { step: 0.1 },
                  InputProps: { endAdornment: <InputAdornment position='end'>%</InputAdornment> },
                }}
                name={'initialMargin'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (formState.productType === ProductTypeEnum.DISCOUNT && Number(value) <= 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.initialRate.label' }),
                  inputProps: { step: 0.1 },
                  InputProps: { endAdornment: <InputAdornment position='end'>%</InputAdornment> },
                  disabled: true,
                }}
                name={'initialRate'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.margin.label' }),
                  inputProps: { step: 0.1 },
                  InputProps: { endAdornment: <InputAdornment position='end'>%</InputAdornment> },
                }}
                name={'margin'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (Number(value) <= 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.productFees.label' }),
                  inputProps: { step: 0.1 },
                }}
                name={'productFees'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (Number(value) < 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.productFeesPercentage.label' }),
                  inputProps: { step: 0.1 },
                }}
                name={'productFeesPercentage'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (Number(value) < 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormNumberField
                textFieldProps={{
                  label: intl.formatMessage({ id: 'product.equityShare.label' }),
                  inputProps: { step: 0.1 },
                  InputProps: { endAdornment: <InputAdornment position='end'>%</InputAdornment> },
                }}
                name={'equityShare'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                  validate: (value: string) => {
                    if (Number(value) < 0) {
                      return intl.formatMessage({ id: 'common.error.minNumber' }, { min: 0 });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormSelect
                name={'productTier'}
                rules={{
                  required: intl.formatMessage({ id: 'common.error.requiredField' }),
                }}
                options={productPoolOptions}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOption: ISelectOption) =>
                    option.value === selectedOption.value,
                }}
                selectInputProps={{
                  label: intl.formatMessage({ id: 'product.productTier.label' }),
                  placeholder: intl.formatMessage({ id: 'placeholders.select' }),
                  InputLabelProps: { shrink: true },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormCalendar
                disablePast
                className={'w-full'}
                name={'launchDate'}
                defaultValue={formState.launchDate}
                label={intl.formatMessage({ id: 'product.launchDate.label' })}
                rules={{
                  validate: (value: Date) => {
                    if (!value) return true;
                    const isBeforeToday = dayjs(value).isBefore(new Date(), 'day');

                    if (isBeforeToday) {
                      return intl.formatMessage({ id: 'common.error.mustBeInFuture' });
                    }
                  },
                }}
              />
            </div>

            <div className={'mt-4'}>
              <FormCalendar
                disablePast
                className={'w-full'}
                name={'closeDate'}
                minDate={formState.launchDate?.add(1, 'day')}
                label={intl.formatMessage({ id: 'product.closeDate.label' })}
                rules={{
                  validate: (value: Date) => {
                    if (!value) return true;
                    const isBeforeToday = dayjs(value).isBefore(new Date(), 'day');
                    const isAfterLaunchDate = dayjs(value).isAfter(formState.launchDate, 'day');

                    if (isBeforeToday) {
                      return intl.formatMessage({
                        id: 'common.error.mustBeInFuture',
                      });
                    }

                    if (!isAfterLaunchDate) {
                      return intl.formatMessage({
                        id: 'product.error.closeDateTooEarly',
                      });
                    }
                    return !!value;
                  },
                }}
              />
            </div>
            <div className={'mt-4 flex items-center'}>
              <FormControlLabel
                control={<FormCheckbox name={'isBrokerOnly'} />}
                label={<FormattedMessage id={'product.isBrokerOnly.label'} />}
              />
            </div>
            <div className={'mt-4 flex items-center'}>
              <FormControlLabel
                control={<FormCheckbox name={'isRestricted'} />}
                label={<FormattedMessage id={'product.isRestricted.label'} />}
              />
            </div>
          </div>
          <LoadingButton isLoading={isLoading} buttonProps={{ variant: 'contained', type: 'submit' }}>
            <FormattedMessage id={'cta.submit'} />
          </LoadingButton>
        </form>
      </FormProvider>
    </div>
  );
};
